import { createTheme, responsiveFontSizes } from "@mui/material/styles";

// Colors converted from Tailwind CSS theme (oklch → hex approximations)
const colors = {
  // Light mode
  background: "#ffffff",
  backgroundPaper: "#f6f8fa",
  backgroundSubtle: "#ffffff",
  foreground: "#0a0a0a",        // oklch(0.145 0 0)
  muted: "#ececf0",
  mutedForeground: "#717182",
  accent: "#e9ebef",
  accentForeground: "#030213",
  destructive: "#d4183d",
  border: "rgba(0, 0, 0, 0.1)",
  inputBackground: "#f3f3f5",

  // Dark mode
  darkBackground: "#161616",    // oklch(0.145 0 0)
  darkBackgroundPaper: "#070f1b",
  darkBackgroundSubtle: "#0d1117",
  darkForeground: "#fafafa",    // oklch(0.985 0 0)
  darkMuted: "#3d3d3d",         // oklch(0.269 0 0)
  darkMutedForeground: "#a3a3a3", // oklch(0.708 0 0)
  darkBorder: "#3d3d3d",

  // MUI-specific (kept from original, no Tailwind equivalent)
  primary: "#c79dcc",
  secondary: "#58a6ff",
  gradientPrimary: "linear-gradient(90deg, #008cff, #06a35a)",
  gradientHover: "linear-gradient(90deg, #ff0033, #ff6600)",
};

export const getTheme = (mode) => {
  const isDark = mode === "dark";

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 480,
        md1: 768,
        md: 900,
        lg: 1024,
        xl: 1280,
        xxl: 1536,
      },
    },
    palette: {
      mode,
      primary: { main: colors.primary },
      secondary: { main: colors.secondary },
      gradients: {
        primary: colors.gradientPrimary,
        hover: colors.gradientHover,
      },
      icon: {
        main: isDark ? colors.darkForeground : colors.foreground,
        size: "1.2rem",
      },
      button: {
        primary: colors.gradientPrimary,
        secondary: isDark ? colors.secondary : "#0056b3",
      },
      background: {
        default: isDark ? colors.darkBackground : colors.background,
        paper: isDark ? colors.darkBackgroundPaper : colors.backgroundPaper,
        subtle: isDark ? colors.darkBackgroundSubtle : colors.backgroundSubtle,
      },
      text: {
        primary: isDark ? colors.darkForeground : colors.foreground,
        secondary: isDark ? colors.darkMutedForeground : colors.mutedForeground,
        heading: isDark ? "#1e7902" : "#0f0202",
        subheading: isDark ? "#bb5fc5" : colors.secondary,
        disabled: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
      },
      divider: isDark ? colors.darkBorder : colors.border,
      action: {
        disabledBackground: isDark ? colors.darkMuted : colors.muted,
      },
    },
    typography: {
      fontFamily: `"Inter", "Roboto", "Arial", sans-serif`,
      // Base font size synced with Tailwind clamp(14px, 1vw + 12px, 18px)
      // MUI uses rem units relative to the html font size, so these stay proportional
      h1: {
        fontFamily: `"Montserrat", sans-serif`,
        fontWeight: 700,
        fontSize: "2.25rem",    // was 3.5rem — too large on mobile
        letterSpacing: "-0.02em",
        lineHeight: 1.5,
      },
      h2: {
        fontFamily: `"Montserrat", sans-serif`,
        fontWeight: 600,
        fontSize: "2rem",       // was 3.25rem
        lineHeight: 1.5,
      },
      h3: {
        fontWeight: 500,
        fontSize: "1.5rem",
        lineHeight: 1.5,
      },
      h4: {
        fontWeight: 500,
        fontSize: "1.125rem",
        lineHeight: 1.5,
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.7,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.7,
        color: isDark ? colors.darkMutedForeground : colors.mutedForeground,
      },
      button: {
        fontWeight: 500,
        fontSize: "1rem",
        lineHeight: 1.5,
        textTransform: "none",
      },
      code: {
        fontFamily: `"Fira Code", monospace`,
        fontSize: "0.9rem",     // was 1.2rem — kept relative so clamp still applies
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
        padding: "2px 4px",
        borderRadius: "4px",
      },
      accent: {
        fontFamily: `"Dancing Script", cursive`,
        fontSize: "2rem",       // was 2.5rem
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            transition: "all 0.3s ease",
            fontSize: "clamp(0.8rem, 1.5vw, 1rem)",
          },
          containedPrimary: ({ theme }) => ({
            background: theme.palette.gradients.primary,
            color: "#fff",
            "&:hover": {
              background: theme.palette.gradients.hover,
              transform: "translateY(-2px)",
              boxShadow: "0px 4px 15px rgba(0, 140, 255, 0.3)",
            },
          }),
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontFamily: "JetBrains Mono, monospace",
            fontWeight: 600,
            color: "#fff",
            background: theme.palette.gradients.primary,
            textDecoration: "none",
            borderRadius: "20px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s ease",
            padding: "8px 0",
            whiteSpace: "nowrap",
            cursor: "pointer",
            border: "none",
            fontSize: "clamp(0.75rem, 1.5vw, 1.05rem)",
            [theme.breakpoints.up("md1")]: {
              paddingLeft: "16px",
              paddingRight: "16px",
              marginLeft: "4px",
              marginRight: "4px",
            },
            [theme.breakpoints.up("lg")]: {
              paddingLeft: "24px",
              paddingRight: "24px",
              marginLeft: "8px",
              marginRight: "8px",
            },
            "&:hover": {
              background: theme.palette.gradients.hover,
              transform: "translateY(-2px)",
              boxShadow: "0px 4px 15px rgba(0, 140, 255, 0.3)",
              textDecoration: "none",
            },
          }),
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            code: "code",
            accent: "span",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: "clamp(0.8rem, 1.5vw, 1rem)",
            backgroundColor: isDark ? colors.darkMuted : colors.inputBackground,
          },
        },
      },
    },
  });

  // Automatically scales h1-h6 down at smaller breakpoints
  return responsiveFontSizes(theme, { factor: 2.5 });
};



